import {useEffect, useMemo, useState} from "react";
import { useDispatch } from "react-redux";
import { useMiniApp, useInitDataRaw, useThemeParams, useLaunchParams } from "@tma.js/sdk-react";
import { viewport, init, isTMA, postEvent } from "@telegram-apps/sdk";
import {setColors, setPlatform, setSafeAreaStore, setTheme} from "../../app/store/slices/appSlice.ts";
import {setGenerations, setUser} from "../../app/store/slices/userSlice.ts";
import { isMobileChecker } from "../../utils/IsMobileChecker.ts";
import { PlatformType } from "../../shared/types/PlatformType.ts";
import { ApiClient } from "../../shared/api/apiClient.ts";
import type { User } from "../../shared/types/User.ts";
import {GenerationApi} from "../api/generationApi.ts";
import {createAppTheme} from "../../app/theme/theme.ts";
import {ColorsTheme} from "../../shared/data/colors.ts";
import {setPaymentProviders, setPaymentPacks} from "../../app/store/slices/presetsSlice.ts";
import {getPaymentProviders} from "../../utils/getPaymentProviders.ts";
import {PaymentApi} from "../api/paymentApi.ts";
import {PaymentProviderEnum} from "../../shared/types/PaymentProviderEnum.ts";
import {PaymentPackType} from "../../shared/types/PaymentPackType.ts";


export const useAppInitialization = () => {
    const dispatch = useDispatch();
    const generationApi = useMemo(() => new GenerationApi(), []);
    const paymentApi = useMemo(() => new PaymentApi(), []);
    const miniApp = useMiniApp();
    const initDataRaw = useInitDataRaw();
    const launchParams = useLaunchParams();
    const themeParams = useThemeParams();
    const colors = ColorsTheme[Math.floor(Math.random() * ColorsTheme.length)];
    const theme = useMemo(() => createAppTheme(themeParams, colors[0]), [themeParams]);
    const platform = launchParams?.platform || 'unknown';
    const [safeArea, setSafeArea] = useState<{ top: number, bottom: number }>({ top: 0, bottom: 0 });
    const [isInitilizing, setIsInitilizing] = useState(true);
    const [authReady, setAuthReady] = useState(false);

    useEffect(() => {
        const initTelegram = async () => {
            if (!await isTMA()) {
                console.log('NOT TMA: ', isTMA)
                setAuthReady(true);
                return
            }
            init()
            console.log(viewport)
            if (viewport.mount.isAvailable()) {
                try {
                    await viewport.mount();
                    if (viewport.requestFullscreen.isAvailable() && isMobileChecker(platform as PlatformType)) {
                        await viewport.requestFullscreen();
                    }
                    // Костыль чтобы virwport успел прогрузиться
                    await new Promise(resolve => setTimeout(resolve, 500));
                    const {top, bottom} = await viewport.safeAreaInsets()
                    setSafeArea({top: top, bottom: bottom})
                    dispatch(setSafeAreaStore({top: top, bottom: bottom}))
                } catch (e) {
                    console.error('viewport error', e);
                }

            }

            postEvent('web_app_toggle_orientation_lock', { locked: true });
        };

        initTelegram();
    }, []);

    useEffect(() => {
        if (miniApp) {
            miniApp.ready();
            miniApp.setHeaderColor('#000000');
        }

    }, [miniApp]);

    useEffect(() => {
        dispatch(setTheme(theme));
        dispatch(setPlatform(platform as PlatformType));
        dispatch(setColors(colors));
        const providers = getPaymentProviders();
        dispatch(setPaymentProviders(providers));

        const loadPaymentPacks = async () => {
            try {
                const packsRecord: Record<PaymentProviderEnum, PaymentPackType[]> = {} as Record<PaymentProviderEnum, PaymentPackType[]>;
                
                await Promise.all(
                    providers.map(async (provider) => {
                        const packs = await paymentApi.getPaymentPacks(provider);
                        packsRecord[provider] = packs;
                    })
                );

                dispatch(setPaymentPacks(packsRecord));
            } catch (error) {
                console.error('Failed to load payment packs:', error);
            }
        };

        loadPaymentPacks();
    }, [dispatch, theme, platform, paymentApi]);

    useEffect(() => {
        const authenticate = async () => {
            const rawInitData = typeof initDataRaw === 'string'
                ? initDataRaw
                : (window as any)?.Telegram?.WebApp?.initData;

            if (!rawInitData || typeof rawInitData !== 'string') return;

            try {
                const apiClient = new ApiClient();
                const { token, user } = await apiClient.postWithInitDataHeader<{ token: string, user: User }>('auth/app/login', rawInitData);
                apiClient.setToken(token);
                dispatch(setUser(user));
                setAuthReady(true);
            } catch (error) {
                console.error('Auth error:', error);
            }
        };

        authenticate();
    }, [dispatch, initDataRaw]);

    useEffect(() => {
        const initializeData = async () => {
            if (!authReady) {
                return;
            }
            try {
                const generations = await generationApi.getAllGenerations();
                dispatch(setGenerations(generations));
            } catch (error) {
                console.error('Initialization error:', error);
            } finally {
                setTimeout(() => setIsInitilizing(false), 300);
            }
        };

        initializeData();
    }, [authReady, dispatch, generationApi]);

    return { safeArea, theme, isInitilizing};
};
